import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { stripePromise, PLAN } from '../config/stripe';
import { auth } from '../utils/firebase';
import './Pricing.css';

const Pricing = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Reset loading state when component mounts (e.g., returning from Stripe)
  useEffect(() => {
    setLoading(false);
  }, []);

  const handleGetStarted = async () => {
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        alert('Please sign in to subscribe');
        navigate('/login');
        return;
      }

      const clubId = user.uid;

      // Get the correct price ID based on billing period
      const priceId = billingPeriod === 'monthly'
        ? PLAN.monthly.priceId
        : PLAN.yearly.priceId;

      // Call Netlify function to create checkout session
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: priceId,
          userId: user.uid,
          userEmail: user.email,
          clubId: clubId,
          tier: 'pitchallo',
          billingPeriod: billingPeriod,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { sessionId } = await response.json();

      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionId,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pricing-page">
      <button className="home-button" onClick={() => navigate('/')}>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
        </svg>
        Home
      </button>

      <div className="pricing-container">
        <div className="pricing-header">
          <h1>One plan. Everything included.</h1>
          <p>Simple, transparent pricing for clubs of all sizes</p>
        </div>

        {/* Billing Period Toggle */}
        <div className="billing-toggle-container">
          <div className="billing-toggle">
            <button
              className={`toggle-option ${billingPeriod === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingPeriod('monthly')}
              aria-label="Switch to monthly billing"
            >
              Monthly
            </button>
            <button
              className={`toggle-option ${billingPeriod === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingPeriod('yearly')}
              aria-label="Switch to yearly billing"
            >
              Yearly
            </button>
          </div>
        </div>

        <div className="pricing-grid pricing-grid-single">
          <div className="pricing-card pricing-card-single">
            <div className="plan-header">
              <div className="plan-name">Pitchallo</div>
              <div className="plan-price">
                <span className="currency">$</span>
                {billingPeriod === 'monthly' ? PLAN.monthly.price : PLAN.yearly.price}
              </div>
              <div className="plan-period">
                {billingPeriod === 'monthly' ? 'per month' : 'per year'}
              </div>
              {billingPeriod === 'yearly' && (
                <div className="savings-badge">
                  Save ${PLAN.yearly.savings.amount} ({PLAN.yearly.savings.months} months free)
                </div>
              )}
            </div>

            <div className="plan-description">
              Everything your club needs to manage pitches, teams and fixtures
            </div>

            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Unlimited pitches</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Unlimited teams</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Unlimited user accounts</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">No restrictions on future allocations</div>
              </div>

              <div className="divider"></div>

              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Weather alerts</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Recurring allocations</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Smart fixture tool</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Share links &amp; share allocations</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Bulk import (CSV/JSON)</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Facility sharing</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Custom club branding</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Print reports + change log</div>
              </div>
              <div className="feature-item">
                <div className="feature-icon check">✓</div>
                <div className="feature-text">Cloud backup</div>
              </div>
            </div>

            <button
              className="cta-button"
              onClick={handleGetStarted}
              disabled={loading}
              aria-label="Get started with Pitchallo"
            >
              {loading ? 'Processing...' : 'Get Started'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
